import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, TrendingUp, DollarSign, Sparkles } from 'lucide-react';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AIUsageTab() {
    const { getAuthHeader } = useAuth();
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    // `7d`/`30d`/`90d` — the shape `usage.py::_parse_period` maps. The page
    // used to send `?days=30`, which the backend never read, so every request
    // silently fell through to the 30-day default and the selector did nothing.
    const [period, setPeriod] = useState('30d');

    useEffect(() => {
        loadUsage();
    }, [period]);

    const loadUsage = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/usage/ai?period=${period}`, { headers: getAuthHeader() });
            setUsage(res.data);
        } catch { toast.error('Failed to load AI usage data'); }
        finally { setLoading(false); }
    };

    if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-stone-400" /></div>;

    const totals = usage?.totals || {};
    const byDay = usage?.by_day || [];
    const byUser = usage?.by_user || [];
    const byFeature = usage?.by_feature || [];

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Usage & Costs</h3>
                    <p className="text-xs text-stone-500 mt-0.5">Monitor your organization's AI token consumption and costs</p>
                </div>
                <select
                    value={period}
                    onChange={e => setPeriod(e.target.value)}
                    className="px-3 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none"
                >
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                </select>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-3 gap-4">
                <div className="bg-stone-50 rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-violet-500" />
                        <span className="text-xs text-stone-500 font-medium">Total Tokens</span>
                    </div>
                    <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {(totals.total_tokens || 0).toLocaleString()}
                    </p>
                </div>
                <div className="bg-stone-50 rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-stone-500 font-medium">Total Cost</span>
                    </div>
                    <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        ${(totals.total_cost || 0).toFixed(4)}
                    </p>
                </div>
                <div className="bg-stone-50 rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        <span className="text-xs text-stone-500 font-medium">API Calls</span>
                    </div>
                    <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {(totals.total_calls || byDay.reduce((sum, d) => sum + 1, 0)).toLocaleString()}
                    </p>
                </div>
            </div>

            {/* Cost info box */}
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                <h4 className="text-xs font-semibold text-violet-800 mb-1">Cost Breakdown (GPT-4o)</h4>
                <p className="text-[10px] text-violet-600 leading-relaxed">
                    Input: $2.50/1M tokens | Output: $10.00/1M tokens | GPT-4o-mini: $0.15/$0.60 per 1M tokens.
                    Costs are tracked per API call and include AI chat, email writing, deal analysis, and agentic personalization.
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* By Feature */}
                <div>
                    <h4 className="text-sm font-semibold text-stone-900 mb-3">Usage by Feature</h4>
                    <div className="space-y-2">
                        {byFeature.length === 0 ? (
                            <p className="text-xs text-stone-400 py-4 text-center">No usage data yet</p>
                        ) : byFeature.map((f, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200">
                                <span className="text-xs font-medium text-stone-700">{(f.feature || 'unknown').replace(/_/g, ' ')}</span>
                                <div className="text-right">
                                    <span className="text-xs font-semibold text-stone-900">{(f.tokens || 0).toLocaleString()} tokens</span>
                                    <span className="text-[10px] text-stone-400 ml-2">${(f.cost || 0).toFixed(4)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* By User */}
                <div>
                    <h4 className="text-sm font-semibold text-stone-900 mb-3">Top Users</h4>
                    <div className="space-y-2">
                        {byUser.length === 0 ? (
                            <p className="text-xs text-stone-400 py-4 text-center">No usage data yet</p>
                        ) : byUser.slice(0, 10).map((u, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200">
                                <span className="text-xs font-medium text-stone-700 truncate max-w-[150px]">{u._id || 'Unknown'}</span>
                                <div className="text-right">
                                    <span className="text-xs font-semibold text-stone-900">{(u.tokens || 0).toLocaleString()} tokens</span>
                                    <span className="text-[10px] text-stone-400 ml-2">${(u.cost || 0).toFixed(4)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Daily Trend */}
            {byDay.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold text-stone-900 mb-3">Daily Usage</h4>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-stone-200">
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium">Date</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">Tokens</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byDay.slice(-14).reverse().map((d, i) => (
                                    <tr key={i} className="border-b border-stone-100">
                                        <td className="py-2 px-3 text-stone-700">{d.date}</td>
                                        <td className="py-2 px-3 text-right text-stone-900 font-medium">{(d.tokens || 0).toLocaleString()}</td>
                                        <td className="py-2 px-3 text-right text-stone-500">${(d.cost || 0).toFixed(4)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AdminAIUsagePage() {
    return (
        <AdminLayout title="AI Usage" description="Monitor AI token consumption and costs">
            <AIUsageTab />
        </AdminLayout>
    );
}
